import { BASE_BPM, WARNING_BEATS } from "./constants";
import type { BeatState } from "./types";

export interface BeatTickResult {
  rotationDue: boolean;
  warningNow: boolean;
  enteredWarning: boolean;
}

export interface BeatAdvanceOptions {
  pauseRotationCountdown?: boolean;
}

export function createBeatState(rotationEveryBeats: number, bpm: number = BASE_BPM): BeatState {
  const beatDurationSec = 60 / bpm;
  return {
    bpm,
    beatDurationSec,
    rotationEveryBeats,
    warningBeats: WARNING_BEATS,
    timeToNextRotationSec: beatDurationSec * rotationEveryBeats,
    lastDownbeatSec: 0,
    songTimeSec: 0
  };
}

export function setRotationInterval(beat: BeatState, rotationEveryBeats: number): void {
  const prevCycleSec = Math.max(0.0001, beat.rotationEveryBeats * beat.beatDurationSec);
  const remainingRatio = Math.min(1, Math.max(0, beat.timeToNextRotationSec / prevCycleSec));
  beat.rotationEveryBeats = rotationEveryBeats;
  const nextCycleSec = Math.max(0.0001, beat.rotationEveryBeats * beat.beatDurationSec);
  beat.timeToNextRotationSec = nextCycleSec * remainingRatio;
  if (beat.timeToNextRotationSec <= 0) {
    beat.timeToNextRotationSec = nextCycleSec;
  }
}

function warningThreshold(beat: BeatState): number {
  return beat.warningBeats * beat.beatDurationSec;
}

export function advanceBeat(
  beat: BeatState,
  dt: number,
  options: BeatAdvanceOptions = {}
): BeatTickResult {
  const wasWarning = beat.timeToNextRotationSec <= warningThreshold(beat);

  beat.songTimeSec += dt;
  beat.lastDownbeatSec = beat.songTimeSec - (beat.songTimeSec % beat.beatDurationSec);
  if (options.pauseRotationCountdown === true) {
    return {
      rotationDue: false,
      warningNow: wasWarning,
      enteredWarning: false
    };
  }

  beat.timeToNextRotationSec -= dt;

  let rotationDue = false;
  const cycle = beat.rotationEveryBeats * beat.beatDurationSec;
  while (beat.timeToNextRotationSec <= 0) {
    beat.timeToNextRotationSec += cycle;
    rotationDue = true;
  }

  const warningNow = beat.timeToNextRotationSec <= warningThreshold(beat);
  return {
    rotationDue,
    warningNow,
    enteredWarning: warningNow && !wasWarning
  };
}

export function beatDeltaAt(beat: BeatState, eventTimeSec: number): number {
  const phase = ((eventTimeSec % beat.beatDurationSec) + beat.beatDurationSec) % beat.beatDurationSec;
  return Math.min(phase, beat.beatDurationSec - phase);
}
